import React, { useEffect, useReducer, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { isNotEmpty, i18nIntl, isColorBright, RGB_2_HEX, isEmpty, UCFirst } from '../../lib/ep';
import { delete_tag, get_all_validators, save_tag_validator } from '../../lib/tag_validators';

import { CreateButton, MainButton, SecondaryButton } from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection';
import FloatingLayer from '../builder/FloatingLayer';
import Input from '../builder/Input';
import { AlertMessage } from '../builder/Message';
import Model from '../builder/Model';
import SaveButton from '../builder/SaveButton';
import SectionTitle from '../builder/SectionTitle';
import Select from '../builder/Select';

import Module from '../ui_items/Module';
import SectionHeader from '../ui_items/SectionHeader';
import RibbonCollection from '../ui_items/RibbonCollection';
import DynamicFields from '../builder/DynamicFields';
import TextArea from '../builder/TextArea';
import AgentCards from '../ui_items/AgentCards';
import ColorPicker from '../builder/ColorPicker';
import ActionLink from '../builder/ActionLink';
import SaveResponseMessage from '../builder/SaveResponseMessage';
import { DeleteButton } from '../builder/ConfirmButton';
import NavigationTabs from '../builder/NavigationTabs';
import Tooltip from '../builder/Tooltip';

export const fields_by_type = {
	store:[
		{ k:'header.store_name', v:"Store name" },
		{ k:'header.store_address', v:"Store address" },
		{ k:'header.tax_id', v:"Tax ID" },
		{ k:'header.phone', v:"Phone" },
	],
	location:[
		{ k:'header.store_name', v:"Store name" },
		{ k:'header.store_address', v:"Store address" },
		{ k:'header.phone', v:"Phone" },
		{ k:'header.postal_code', v:"Postal code" },
		{ k:'header.city', v:"City" },
		{ k:'header.country', v:"Country" },
		{ k:'header.country_code', v:"Country code" },
	],
	article:[
		{ k:'line_items.*.article', v:"Description" },
		{ k:'line_items.*.sku', v:"SKU (article reference)" },
	],
};

const reducer = (state, action) => {
	switch (action.type) {
		case 'SET_VALIDATORS':
			return {
				...state,
				validators:action.validators,
			};

		case 'CHANGE_TAG':
			let errors = {}
			const configs = state?.tag?.OcrTag?.configs ?? [];
			
			let empty_fields = false;
			configs.map((c) => {
				if(isEmpty(c, 'field')) empty_fields = true;
			});

			if(empty_fields) errors['field'] = 'Empty fields';

			return {
				...state,
				tag:{
					OcrTag:{
						...state.tag.OcrTag,
						[action.k]:action.v,
					},
				},
				errors,
			};

		case 'CHANGE_TAG_ID':
			return {
				...state,
				validators: state.validators.map((tag) => {
					if (tag.OcrTag.id != action.payload.id) return tag;
					return action.payload.tag;
				})
			};

		case 'ADD_UPDATE_TAG':
			const exists_tag = state.validators.find((t) => t.OcrTag.id == action.tag.OcrTag.id);

			if(!exists_tag) {
				return {
					...state,
					validators:[...state.validators, action.tag],
					tag:false,
					edit_tag:false,
				};
			}

			return {
				...state,
				validators: state.validators.map((tag) => {
					if(tag.OcrTag.id != action.tag.OcrTag.id) return tag;
					return action.tag;
				}),
				tag:false,
				edit_tag:false,
			};

		case 'DELETE_TAG':
			return {
				...state,
				validators: state.validators.filter((tag) => {
					return (tag.OcrTag.id != action.tag_id)
				}),
				edit_tag:false,
			};
		case 'TAG_IN_USE':
			return {
				...state,
				in_use:!state.in_use,
			};

		case 'EDIT_TAG':
			return {
				...state,
				tag:(action.id !== false) ? state.validators.find((tag) => tag.OcrTag.id == action.id) : false,
				edit_tag:(action.id !== false) ? true : false,
			};

		case 'TAG_MATCHES_DETAIL':
			const field_id = action?.field_id ?? false;

			return {
				...state,
				tag:(action.id !== false) ? state.validators.find((tag) => tag.OcrTag.id == action.id) : false,
				tag_field_detail:field_id,
			};

		case 'CREATE_TAG':
			return {
				...state,
				tag:{
					OcrTag:{
						name:"",
					},
				},
				edit_tag:true,
				error:false,
			};

		case 'CHANGE_TAB':
			return {
				...state,
				actual_tab:action.id,
			};

		default:
			return state;
	}
};

const Wrapper = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		<>
			<SectionHeader
				title={i18n("Receipt Tags Management")}
				subtitle={<>
					<p>{i18n("Configure and manage your Receipt Tags to automate the validation of purchase receipts. Define custom rules to detect store names, locations, and purchased products using OCR and AI. Use these tags to set validation conditions, assign rewards, and streamline data integration for advanced analysis.")}</p>
				
					<p><ActionLink
						_href={`${window.zendesk_base_url}34968848348308`}
						_target='_blank'
						_class={{ other:'gui_not_in_top_small' }}
					>Help tutorial</ActionLink></p>
				</>}
				total={props.total}
				icon={{ icon:'knk-tag' }}
			>
				<ButtonCollection>
					{props.actions}
				</ButtonCollection>
			</SectionHeader>

			{props.children}
		</>
	)
};

const FieldMatchesEdit = ({ tag, change, save, ...props }) => {
	const field_id = props?.field_id ?? false;
	const config = tag.configs.find((c) => (c.id == field_id));
	const matches = config?.matches ?? [];

	const changeNL = (k, v) => {
		change('configs', tag.configs.map((t) => {
			if(t.id != field_id) return t;

			t.matches = v.split("\n");
			return t;
		}));
	};

	return (
		<FloatingLayer
			_class={{ size:'small' }}
			backButton={props.backButton}
		>
			<div>
				<Model model={config} change={changeNL}>
					<TextArea
						value={matches.join("\n")}
						name="matches"
						_class={{
							first:true,
							v_size:'huge',
						}}
					>Values</TextArea>
				</Model>

				<ButtonCollection
					in_floating={true}
				>
					<MainButton
						onClick={save}
					>Save changes</MainButton>

					<SecondaryButton
						onClick={props.backButton}
					>Close</SecondaryButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const TagConditions = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors, setErrors ] = useState({});

	const matches = model?.matches ?? [];

	const validateMatches = (v) => {
		const _errors = {};

		if(v.length == 0) {
			if(props.onValidate != undefined) props.onValidate(false);
			setErrors({ matches:i18n("This cannot be empty.") });
			return;
		}
		
		const wrong_matches = [];
		v.map((match) => {
			if(match.length < 2) wrong_matches.push(match);
		});

		if(wrong_matches.length > 0) {
			_errors.matches = i18n("Each match must be, at least, 2 characters long.");
		}

		if(props.onValidate != undefined) props.onValidate((wrong_matches.length > 0) ? false : true);

		setErrors(_errors);
	};

	const changeNL = (k, v) => {
		if(k == 'matches') {
			const matches_lines_temp = v.split("\n");

			change(k, matches_lines_temp);
			validateMatches(matches_lines_temp.filter((match) => (match.length > 0)));
			return;
		}

		change(k, v);
	};

	const available_fields = (fields_by_type[props.tag.type].length > 1) ? [{ k:'', v:`-- ${i18n("Select")} --` }, ...fields_by_type[props.tag.type]] : fields_by_type[props.tag.type];

	const selected_field = model?.field ?? available_fields[0].k;

	return (
		<div>
			<Model model={model} change={changeNL} errors={errors}>
				{(available_fields.length > 1) && (
					<Select
						name='field'
						values={available_fields}
						_class={{ size:70 }}
						value={selected_field}
					>Field</Select>
				)}

				{(selected_field != '' && model.type == 'contains') && (
					<TextArea
						value={matches.join("\n")}
						name="matches"
						label_props={{
							tooltip:<Tooltip>Enter one word or phrase per line. If the system detects any of them in the specified field of the purchase ticket, the corresponding tag will be automatically assigned. Uppercase and lowercase letters are not distinguished, so there's no need to write the same word with different letter cases.</Tooltip>,
						}}
					>Values</TextArea>
				)}

				{(selected_field != '' && model.type == 'regex') && (
					<Input
						value={matches}
						name="matches"
					>Regex</Input>
				)}
			</Model>
		</div>
	)
};

export const getTagColorByType = (tag) => {
	if(isNotEmpty(tag, 'color')) return tag.color;

	switch(tag.type) {
		case 'store':
			return 'rgba(120, 195, 0, 1)';

		case 'location':
			return 'rgba(133, 66, 142, 1)';

		case 'article':
			return 'rgba(255, 152, 0, 1)';
	}

	return 'rgba(30, 34, 170, 1)';
};

export const getTagTitleByType = (tag, is_short = false) => {
	switch(tag.type) {
		case 'store':
			return (is_short !== false) ? "Store" : "Tag Store";

		case 'location':
			return (is_short !== false) ? "Location" : "Tag Location";

		case 'article':
			return (is_short !== false) ? "Article" : "Tag Article";
	}

	return "Unknown";
};

const TagEdit = ({ tag, change, edit, save, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ matches_validate, setMatchesValidate ] = useState(true);

	const formatSimpleListOfItems = (items = [], num_items = 3) => {
		const sliced_items = items.slice(0, num_items);
		return `"${sliced_items.join("\", \"")}"${(items.length > num_items) ? '...' : ''}`;
	};

	const onTagValidate = (status) => {
		setMatchesValidate(status);
	};

	const dynamic_fields = [{
		type:'contains',
		label:"Contains",
		icon:'knk-list-items',
		add:(f) => {
			if(tag.type == 'article') {
				return {
					...f,
					field:fields_by_type.article[0].k,
				};
			}

			return f;
		},
		title:(f) => {
			const real_matches = f.matches ?? [];
			const matches = real_matches.slice(0, 3);

			if(f.field == undefined) return i18n("Rule");

			if(tag.type == 'article') {
				if(matches.length == 0) return `[ ${i18n("Empty")} ]`;
				return formatSimpleListOfItems(real_matches);
			}

			const field = fields_by_type[tag.type].find((t) => (t.k == f.field));
			if(matches.length == 0) return i18n(field.v);
			return `${i18n(field.v)}: ${formatSimpleListOfItems(real_matches)}`;
		},
	}];

	const is_edit = (tag.id != undefined) ? true : false;

	let type_change_enabled = true;
	if((tag?.configs ?? []).length > 0) type_change_enabled = false
	if(tag.id != undefined) type_change_enabled = false

	return (
		<FloatingLayer
			_class={{ size:'small' }}
			backButton={() => edit(false)}
		>
			<div>
				<SectionTitle>{(is_edit) ? "Edit tag" : "Create tag"}</SectionTitle>

				<Model
					model={tag}
					change={change}
					errors={props.errors}
				>
					<Select
						name="type"
						disabled={(type_change_enabled) ? false : true}
						values={[
							{ k:'', v:`-- ${i18n("Select")} --` },
							{ k:'store', v:i18n("Store tag") },
							{ k:'location', v:i18n("Location tag") },
							{ k:'article', v:i18n("Article tag") },
						]}
						_class={{ first:true, size: 60 }}
					>Tag type</Select>

					<Input
						name="name"
						placeholder="Enter the name for the tag"
						_class={{ size:60 }}
					>Tag</Input>

					{/*<AutoPostImageUploader
						small_input={true}
						auto_url={`/accounts/auto_img/`}
						auto_type='ocr_validator'
						image={tag.full_image}
						name="image"
						_size={{ width:400, height:400 }}
						_class={{ size:60 }}
					>Image</AutoPostImageUploader>*/}

					<ColorPicker
						name="color"
						color={getTagColorByType(tag)}
						_class={{ size:60 }}
						_hide_code={true}
					>Color</ColorPicker>
				</Model>

				{isNotEmpty(tag, 'type') && (
					<DynamicFields
						name="configs"
						section_title="Rules"
						section_icon='knk-filter'
						add_field_label="Add rule"
						sortable={false}
						can_copy={false}
						fields={tag.configs ?? []}
						field_types={dynamic_fields}
						onChange={(v) => change('configs', v)}
						section_classes='gui_not_in_top_big'
					>
						<TagConditions
							tag={tag}
							intl={props.intl}
							onValidate={onTagValidate}
						/>
					</DynamicFields>
				)}

				{(props.error) && (
					<AlertMessage _class={{ other:'in_bottom' }}>
						<p>{props.error}</p>
					</AlertMessage>
				)}

				{(Object.keys(props.errors).length > 0) && (
					<AlertMessage _class={{ other:'in_bottom' }}>
						{Object.keys(props.errors).map(key => (
							<p key={key}>{props.errors[key]}</p>
						))}
					</AlertMessage>
				)}

				<ButtonCollection in_floating={true}>
					<SaveButton
						disabled={(Object.keys(props.errors).length > 0) || matches_validate === false}
						onClick={save}
					>Continue</SaveButton>

					<SecondaryButton
						onClick={() => edit(false)}
					>Cancel</SecondaryButton>

					<div style={{ flexGrow:'1' }}>
						<DeleteButton
							onClick={() => props.deleteTag(tag.id)}
							squared={true}
							_position='top'
						/>
					</div>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

export const chargebee_item = (plan, currency) => {
	const pay_mode_string = (plan.pay_mode == 0) ? 'Monthly' : 'Yearly';
	let plan_type_string = "ADDITIONAL-DOMAIN";
	return `${plan_type_string}-${currency}-${pay_mode_string}`;
};

const TagValidators = ({ brands, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	if(brands == undefined) brands = [];

	const [ state, dispatch ] = useReducer(reducer, {
		validators:false,
		tag:false,
		brand_id:false,
		edit_tag:false,
		error:false,
		errors:{},
		error_chargebee:false,
		tag_field_detail:false,
		in_use:false,
		actual_tab:'article',
	});

	const { loading, data:validators } = useQuery('tag_validators', () => get_all_validators(), { refetchOnWindowFocus:true });
	useEffect(() => {
		if(!validators) return;
		dispatch({ type:'SET_VALIDATORS', validators });
	}, [ validators ]);

	const edit = (id) => {
		dispatch({
			type:'EDIT_TAG',
			id,
		});
	};

	const createTag = () => {
		dispatch({
			type:'CREATE_TAG',
		});
	};

	const deleteTag = (id) => {
		delete_tag(id,(response)=>{
			if(response.status == 0) {
				dispatch({
					type:'TAG_IN_USE',
					
				});
				setTimeout(() => {
					dispatch({
						type:'TAG_IN_USE',
						
					});
				},3000)
				return;
			}

			dispatch({
				type:'DELETE_TAG',
				tag_id:id,
			});
		});
	};

	const viewMatchesDetail = (tag_id, field_id) => {
		dispatch({
			type:'TAG_MATCHES_DETAIL',
			id:tag_id,
			field_id,
		});
	};

	const change = (k, v) => {
		dispatch({
			type:'CHANGE_TAG',
			k,
			v,
		});
	};

	const validateMatches = (matches) => {
		const matches_ok = [];
		matches.map((match) => {
			const match_ok = match.trim();
			if(match_ok.length > 2) matches_ok.push(match_ok);
		});

		return matches_ok;
	};

	const save_tag = (e, cb) => {
		const _tag = { ...state.tag };
		_tag.OcrTag.configs.map((t) => {
			t.matches = validateMatches(t.matches);
		});

		save_tag_validator(_tag, (response) => {
			if(cb != undefined) cb();

			dispatch({
				type:'ADD_UPDATE_TAG',
				tag:response.tag,
			});
		});
	};

	const deleteRule = (tag_id, rule_id, cb) => {
		const tag = state.validators.find((tag) => tag.OcrTag.id == tag_id)
		save_tag_validator({
			OcrTag:{
				...tag.OcrTag,
				configs:tag.OcrTag.configs.filter((c) => (c.id != rule_id)),
			}
		}, (response) => {
			if(cb != undefined) cb();
			dispatch({
				type:'ADD_UPDATE_TAG',
				tag:response.tag,
			});
		});
	};

	const max_ribbons_to_display = 15;

	const type_cards = [];
	if(state.validators != false) {
		state.validators.map((_item) => {
			const item = _item.OcrTag;

			const image = item?.full_image ?? false;
			const color = getTagColorByType(item);
			let icon = null;
			switch(item.type) {
				case 'store':
					icon = 'knk-get-users-to-pos';
					break;

				case 'location':
					icon = 'knk-city';
					break;

				case 'article':
					icon = 'knk-present-fill';
					break;
			}

			const description = [];
			(item?.configs ?? []).map((c) => {
				const ribbons = [];
				const matches = c?.matches ?? [];

				const field = fields_by_type[item.type].find((i) => (i.k == c.field));
				ribbons.push({
					title:field?.v ?? i18n("Unknown"),
					autotranslate:false,
					color:'--main-bg-color',
				});

				const values = [];
				matches.slice(0, max_ribbons_to_display).map((m) => {
					values.push({
						title:m,
						autotranslate:false,
						color:'--main-text-color',
					});
				});

				if(values.length == 0) {
					values.push({
						title:`-- ${i18n("Empty")} --`,
						autotranslate:false,
						color:'transparent',
					});
				}

				if(matches.length > max_ribbons_to_display) {
					values.push({
						title:`+${matches.length - max_ribbons_to_display}`,
						autotranslate:false,
						color:'gray',
					});
				}

				values.push({
					//title:"Edit values",
					icon:'knk-edit only_icon',
					color:'gray',
					onClick:() => {
						viewMatchesDetail(item.id, c.id);
					},
				});

				/*values.push({
					title:"Delete",
					icon:'knk-delete redIcon',
					color:'lightgray',
					link_type:'confirm',
					onClick:(e, cb) => deleteRule(item.id, c.id, cb),
				});*/

				description.push({
					ribbons,
					values,
				});
			});

			const tag_bg_color = (color.indexOf("rgb") > -1 || color.indexOf("#") > -1) ? color : false;
			const tag_text_color = (tag_bg_color !== false) ? ((isColorBright(RGB_2_HEX(tag_bg_color))) ? 'black' : 'white') : 'white';

			type_cards.push({
				id:item.id,
				icon:(image === false) ? icon : null,
				image:(image !== false) ? image : null,
				full_image:(image !== false) ? image : null,
				type:item.type,
				title:(isNotEmpty(item, 'name')) ? item.name : `[ ${i18n("No mame")} ]`,
				description:<ul style={{ display:'flex', flexDirection:'column', gap:'1em' }}>
					{description.map((desc, d) => {
						return (
							<li key={`desc_${item.id}_${d}`}>
								<RibbonCollection
									items={desc.ribbons}
									_class={{ other:'gui_flex_center gui_flex_align_center' }}
								/>

								<RibbonCollection
									items={desc.values}
									_class={{ other:'gui_flex_center gui_flex_align_center' }}
								/>
							</li>
						)
					})}
				</ul>,
				role:<span
					class={`${tag_text_color}Color`}
					style={{ backgroundColor:(tag_bg_color !== false) ? color : `rgba(var(--ep-${color}-color), 1)` }}
				>{i18n(getTagTitleByType(item, true))}</span>,
				not_overflowed:true,
				whole_actions:<ButtonCollection>
					<MainButton
						onClick={() => edit(item.id)}
					>Edit tag</MainButton>

					{/*<ConfirmButton
						icon='knk-delete'
						color='red'
						squared={true}
						_position='left'
						onClick={() => deleteTag(item.id)}
						tooltip={<Tooltip
							with_icon={false}
							position='top'
						>Delete tag</Tooltip>}
						aria-label="Delete tag"
					/>*/}
				</ButtonCollection>,
			});
		});
	}

	if(type_cards.length == 0) {
		return (
			<Wrapper
				actions={<CreateButton
					onClick={createTag}
				>Create tag</CreateButton>}
				intl={props.intl}
			>
				<Module
					title="No Receipt Tags created yet"
					subtitle="Receipt Tags help you automate the validation of purchase receipts in promotions that require users to upload a receipt to participate."
					_class={{ other:'not_in_top' }}
					with_shadow={false}
				>
					<p>
						<FormattedMessage
							id="Using {ai}, Receipt Tags identify key details like store names, locations, or purchased products."
							values={{
								ai:<strong>{i18n("AI-powered recognition")}</strong>
							}}
						/>
					</p>

					<div>
						<p>{i18n("With Receipt Tags, you can")}:</p>

						<ul class="bulleted_list">
							<li>
								<strong>{i18n("Set validation rules")}:</strong> {i18n("Ensure receipts meet specific criteria before approval.")}
							</li>

							<li>
								<strong>{i18n("Reward participants")}:</strong> {i18n("Assign prizes or points based on detected purchases.")}
							</li>

							<li>
								<strong>{i18n("Streamline data analysis")}:</strong> {i18n("Structure receipt data for easy integration with external systems.")}
							</li>
						</ul>
					</div>

					<p>
						<strong>{i18n("Need help?")}</strong>&nbsp;
						
						<FormattedMessage
							id="Check out our {link}."
							values={{
								link:<ActionLink>tutorial to validate receipt with IA to enter a promotion</ActionLink>,
							}}
						/>
					</p>
				</Module>

				{(state.edit_tag !== false && state.tag_field_detail === false) && (
					<TagEdit
						tag={state.tag?.OcrTag ?? {}}
						error={state.error}
						errors={state.errors}
						change={change}
						edit={edit}
						deleteTag={deleteTag}
						save={save_tag}
						editHTMLContents={(id) => editPage(id)}
						intl={props.intl}
					/>
				)}
			</Wrapper>
		)
	}

	const sections = [{
		label:`${i18n("Article")} [${type_cards.filter((c) => (c.type == 'article')).length}]`,
		onClick:() => dispatch({ type:'CHANGE_TAB', id:'article' }),
		icon:'knk-present-fill',
		selected:(state.actual_tab == 'article') ? true : false,
	}, {
		label:`${i18n("Store")} [${type_cards.filter((c) => (c.type == 'store')).length}]`,
		onClick:() => dispatch({ type:'CHANGE_TAB', id:'store' }),
		icon:'knk-get-users-to-pos',
		selected:(state.actual_tab == 'store') ? true : false,
	}, {
		label:`${i18n("Location")} [${type_cards.filter((c) => (c.type == 'location')).length}]`,
		onClick:() => dispatch({ type:'CHANGE_TAB', id:'location' }),
		icon:'knk-city',
		selected:(state.actual_tab == 'location') ? true : false,
	}];

	const filtered_cards = type_cards.filter((c) => (c.type == state.actual_tab));

	return (
		<Wrapper
			actions={<CreateButton
				onClick={createTag}
			>Create tag</CreateButton>}
			intl={props.intl}
		>
			<NavigationTabs
				active={state.actual_tab}
				sections={sections}
			/>

			<Module
				with_shadow={false}
				_class={{ other:'agents_manager gui_is_not_padded not_in_top' }}
				style={{ background:'transparent' }}
			>
				{(filtered_cards.length > 0) ? (
					<AgentCards
						items={filtered_cards}
						num_cols='4'
					/>
				) : (
					<div class="gui_not_in_top" >
						{props.intl.formatMessage({
							id:"No {type} tags were created yet.",
						}, {
							type:i18n(UCFirst(state.actual_tab), 'lowercase'),
						})}
					</div>
				)}
			</Module>

			{(state.edit_tag !== false && state.tag_field_detail === false) && (
				<TagEdit
					tag={state.tag?.OcrTag ?? {}}
					error={state.error}
					errors={state.errors}
					change={change}
					edit={edit}
					deleteTag={deleteTag}
					save={save_tag}
					editHTMLContents={(id) => editPage(id)}
					intl={props.intl}
				/>
			)}

			{(state.tag !== false && state.tag_field_detail !== false) && (
				<FieldMatchesEdit
					tag={state.tag?.OcrTag ?? {}}
					change={change}
					save={save_tag}
					field_id={state.tag_field_detail}
					backButton={() => viewMatchesDetail(false, false)}
				/>
			)}

			{(state.in_use) && (
				<SaveResponseMessage
					status='ko'
					message={<span>{i18n("You can't delete this tag because it is in use.")}</span>}
				/>
			)}
		</Wrapper>
	)
};

export default injectIntl(TagValidators);